import { useEffect, useRef } from 'react'

import { OwnerSquad, ScreenName } from '@/analytics/generated/segment'
import { useAnalytics } from '@/hooks/useAnalytics'
import type { SplitExperimentVariant } from '@/hooks/useSplitExperiment'

export const MANUAL_EXPERIMENT_TRIGGERED_EVENT_NAME =
  'Manual Experiment Triggered'

type UseManualExperimentExposureProps = {
  deduplicationKey?: string
  experimentName: string
  isReady: boolean
  owner: string
  ownerSquad: OwnerSquad
  screenName: ScreenName
  variantId: SplitExperimentVariant
  variantName: SplitExperimentVariant
}

const trackedExposureKeys = new Set<string>()

export const clearManualExperimentExposureDeduplication = () => {
  trackedExposureKeys.clear()
}

const useManualExperimentExposure = ({
  deduplicationKey,
  experimentName,
  isReady,
  owner,
  ownerSquad,
  screenName,
  variantId,
  variantName,
}: UseManualExperimentExposureProps) => {
  const analytics = useAnalytics()
  const exposureKeyRef = useRef<string | null>(null)

  useEffect(() => {
    if (!isReady) return

    const exposureKey =
      deduplicationKey ?? `${experimentName}:${screenName}:${variantName}`

    if (exposureKeyRef.current === exposureKey) return
    if (trackedExposureKeys.has(exposureKey)) return

    const trackExposure = async () => {
      const wasTracked = await analytics(
        MANUAL_EXPERIMENT_TRIGGERED_EVENT_NAME,
        {
          experiment_name: experimentName,
          owner,
          owner_squad: ownerSquad,
          screen_name: screenName,
          variant_id: variantId,
          variant_name: variantName,
        }
      )

      if (!wasTracked) return

      exposureKeyRef.current = exposureKey
      trackedExposureKeys.add(exposureKey)
    }

    void trackExposure()
  }, [
    analytics,
    deduplicationKey,
    experimentName,
    isReady,
    owner,
    ownerSquad,
    screenName,
    variantId,
    variantName,
  ])
}

export default useManualExperimentExposure
